import { TaskUtils } from "@project-mgmt/entities";
export class TaskService {
    /**
     * Calculate task metrics from a list of tasks
     */
    static calculateMetrics(tasks) {
        const total = tasks.length;
        const completed = tasks.filter((task) => task.status === "complete").length;
        const inProgress = tasks.filter((task) => task.status === "in_progress").length;
        const todo = tasks.filter((task) => task.status === "to_do").length;
        const overdue = tasks.filter(TaskUtils.isOverdue).length;
        const dueSoon = tasks.filter(TaskUtils.isDueSoon).length;
        const completionRate = total > 0 ? (completed / total) * 100 : 0;
        return {
            total,
            completed,
            inProgress,
            todo,
            overdue,
            dueSoon,
            completionRate: Math.round(completionRate * 100) / 100,
        };
    }
    /**
     * Filter tasks based on criteria
     */
    static filterTasks(tasks, filters) {
        let filtered = [...tasks];
        if (filters.status) {
            filtered = filtered.filter((task) => task.status === filters.status);
        }
        if (filters.priority) {
            filtered = filtered.filter((task) => task.priority === filters.priority);
        }
        if (filters.assignedTo) {
            filtered = filtered.filter((task) => task.assignedTo === filters.assignedTo);
        }
        if (filters.projectId) {
            filtered = filtered.filter((task) => task.projectId === filters.projectId);
        }
        return filtered;
    }
    /**
     * Sort tasks based on criteria
     */
    static sortTasks(tasks, sortBy = "createdAt", sortOrder = "desc") {
        const sorted = [...tasks];
        sorted.sort((a, b) => {
            let aValue;
            let bValue;
            switch (sortBy) {
                case "dueDate":
                    aValue = a.dueDate ? new Date(a.dueDate).getTime() : 0;
                    bValue = b.dueDate ? new Date(b.dueDate).getTime() : 0;
                    break;
                case "priority":
                    const priorityOrder = { high: 3, medium: 2, low: 1 };
                    aValue = priorityOrder[a.priority];
                    bValue = priorityOrder[b.priority];
                    break;
                case "status":
                    const statusOrder = { to_do: 1, in_progress: 2, complete: 3 };
                    aValue = statusOrder[a.status];
                    bValue = statusOrder[b.status];
                    break;
                case "createdAt":
                default:
                    aValue = new Date(a.createdAt).getTime();
                    bValue = new Date(b.createdAt).getTime();
                    break;
            }
            if (sortOrder === "asc") {
                return aValue - bValue;
            }
            else {
                return bValue - aValue;
            }
        });
        return sorted;
    }
    /**
     * Group tasks by status
     */
    static groupByStatus(tasks) {
        return {
            to_do: tasks.filter((task) => task.status === "to_do"),
            in_progress: tasks.filter((task) => task.status === "in_progress"),
            complete: tasks.filter((task) => task.status === "complete"),
        };
    }
    /**
     * Group tasks by priority
     */
    static groupByPriority(tasks) {
        return {
            high: tasks.filter((task) => task.priority === "high"),
            medium: tasks.filter((task) => task.priority === "medium"),
            low: tasks.filter((task) => task.priority === "low"),
        };
    }
    /**
     * Get tasks that need attention (overdue or due soon)
     */
    static getTasksNeedingAttention(tasks) {
        return tasks.filter((task) => TaskUtils.isOverdue(task) || TaskUtils.isDueSoon(task));
    }
    /**
     * Get tasks assigned to a specific user
     */
    static getTasksForUser(tasks, userId) {
        return tasks.filter((task) => task.assignedTo === userId);
    }
    /**
     * Get tasks created by a specific user
     */
    static getTasksByCreator(tasks, userId) {
        return tasks.filter((task) => task.createdBy === userId);
    }
    /**
     * Calculate project progress based on tasks
     */
    static calculateProjectProgress(tasks) {
        if (tasks.length === 0)
            return 0;
        const completedTasks = tasks.filter((task) => task.status === "complete").length;
        return Math.round((completedTasks / tasks.length) * 100);
    }
    /**
     * Get task status color class for UI
     */
    static getStatusColorClass(status) {
        const colorMap = {
            to_do: "bg-gray-100 text-gray-800",
            in_progress: "bg-blue-100 text-blue-800",
            complete: "bg-green-100 text-green-800",
        };
        return colorMap[status];
    }
    /**
     * Get priority color class for UI
     */
    static getPriorityColorClass(priority) {
        const colorMap = {
            low: "bg-green-100 text-green-800",
            medium: "bg-yellow-100 text-yellow-800",
            high: "bg-red-100 text-red-800",
        };
        return colorMap[priority];
    }
    /**
     * Format due date for display
     */
    static formatDueDate(dueDate) {
        if (!dueDate)
            return "No due date";
        const date = new Date(dueDate);
        const now = new Date();
        const diffTime = date.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays < 0) {
            return `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? "s" : ""}`;
        }
        else if (diffDays === 0) {
            return "Due today";
        }
        else if (diffDays === 1) {
            return "Due tomorrow";
        }
        else if (diffDays <= 7) {
            return `Due in ${diffDays} days`;
        }
        else {
            return date.toLocaleDateString();
        }
    }
    /**
     * Validate task form data
     */
    static validateTaskForm(data) {
        const errors = [];
        if (!data.title || data.title.trim().length === 0) {
            errors.push("Title is required");
        }
        if (data.title && data.title.length > 200) {
            errors.push("Title must be less than 200 characters");
        }
        if (data.dueDate) {
            const dueDate = new Date(data.dueDate);
            if (isNaN(dueDate.getTime())) {
                errors.push("Invalid due date");
            }
        }
        if (data.amount !== undefined && data.amount < 0) {
            errors.push("Amount must be positive");
        }
        return {
            isValid: errors.length === 0,
            errors,
        };
    }
    /**
     * Generate task summary for notifications
     */
    static generateTaskSummary(task) {
        const priority = task.priority === "high" ? "🔴" : task.priority === "medium" ? "🟡" : "🟢";
        const status = task.status === "complete" ? "✅" : task.status === "in_progress" ? "🔄" : "📋";
        return `${priority} ${status} ${task.title}`;
    }
}
